import { useCallback, useEffect, useState } from "react";
import { STORAGE_KEYS } from "./storageKeys";

// Light/dark theme for the main Omni Hub chrome. Persisted per browser; defaults
// to the OS preference. The chosen theme is applied as a `data-theme` attribute on
// <html>, which flips the CSS custom properties in index.css — so page content and
// body-portaled popovers (presence/CLI dropdowns, chat widget) all theme together.
export type Theme = "light" | "dark";

function readInitial(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.theme);
    if (v === "dark" || v === "light") return v;
  } catch { /* ignore */ }
  try {
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  } catch { /* ignore */ }
  return "light";
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(readInitial);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem(STORAGE_KEYS.theme, theme); } catch { /* ignore */ }
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return { theme, toggle };
}

// Read-only view of the current app theme, for surfaces that must mirror it but
// don't own the toggle — notably the DevHub public canvas (`.devhub-public`),
// whose brand tokens are self-contained and only flip when it carries a `.dark`
// class. The TopBar's useTheme() is the single writer (it stamps data-theme on
// <html>); this observes that attribute so those surfaces re-render the instant
// the toggle flips.
export function useAppTheme(): Theme {
  const read = (): Theme => (document.documentElement.dataset.theme === "dark" ? "dark" : "light");
  const [theme, setTheme] = useState<Theme>(read);
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setTheme(read());
    sync(); // catch a value set between initial render and this effect
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return theme;
}
